import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { api } from '@/lib/api';
import { Activity, Clock, CheckCircle2, Shield } from 'lucide-react';

function fmtTime(s) {
  if (!s) return '0m';
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

export default function Study() {
  const { t } = useLang();
  const { teams } = useTeams();
  const [byTeam, setByTeam] = useState({});  // {team_id: {players, name, category}}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!teams || teams.length === 0) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      const acc = {};
      await Promise.all(teams.map(async (team) => {
        try {
          const r = await api.get(`/study/team/${team.id}`);
          if (!cancelled) acc[team.id] = { players: r.data.players || [], name: team.name, category: team.category };
        } catch {
          if (!cancelled) acc[team.id] = { players: [], name: team.name, category: team.category };
        }
      }));
      if (!cancelled) { setByTeam(acc); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [teams]);

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_study')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('study_h1')}</h1>
        <p className="text-gray-400 text-sm mt-2">{t('study_all_teams_subtitle') || 'Vista global de todos tus equipos y jugadores.'}</p>
      </div>

      {loading && <div className="text-gray-400">{t('loading')}…</div>}

      {!loading && teams.length === 0 && (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gold/30 rounded-lg">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-h uppercase tracking-wider text-lg">{t('study_empty')}</p>
        </div>
      )}

      {!loading && teams.map(team => {
        const data = byTeam[team.id] || { players: [] };
        return (
          <div key={team.id} data-testid={`study-team-${team.id}`} className="mb-8 bg-card-steel border border-gold/30 rounded-lg overflow-hidden">
            <div className="bg-navy-deep border-b border-gold/30 px-4 py-3 flex items-center gap-3">
              <Shield className="w-5 h-5 text-gold" />
              <div>
                <div className="font-h uppercase tracking-wider text-lg">{team.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gold">{t(`team_cat_${team.category || 'mixed'}`)} · {data.players.length} {t('study_players_count') || 'jugadores'}</div>
              </div>
            </div>
            {data.players.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">{t('study_team_empty') || 'Aún no hay actividad en este equipo'}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-navy-deep/50 border-b border-gold/20">
                  <tr className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">
                    <th className="px-4 py-2 text-left">{t('study_player')}</th>
                    <th className="px-4 py-2 text-right">{t('study_time')}</th>
                    <th className="px-4 py-2 text-right">{t('study_viewed')}</th>
                    <th className="px-4 py-2 text-right">{t('study_completed')}</th>
                    <th className="px-4 py-2 text-right">{t('study_progress')}</th>
                    <th className="px-4 py-2 text-right">{t('study_last_seen')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold/10">
                  {data.players.map(p => (
                    <tr key={p.roster_id} data-testid={`study-row-${p.roster_id}`} className="hover:bg-gold/5">
                      <td className="px-4 py-2 flex items-center gap-3">
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.name} className="w-8 h-8 rounded-full object-cover border border-gold/30" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-xs">
                            {p.name?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        <span className="font-h uppercase tracking-wider text-xs">{p.name}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs"><Clock className="w-3 h-3 inline text-gold mr-1" />{fmtTime(p.total_time_seconds)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{p.plays_viewed}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs"><CheckCircle2 className="w-3 h-3 inline text-emerald-400 mr-1" />{p.plays_completed}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-block w-20 bg-navy-deep border border-gold/30 rounded-full overflow-hidden">
                          <div className="bg-gold h-2" style={{ width: `${p.avg_progress_pct || 0}%` }}></div>
                        </div>
                        <span className="ml-2 text-[10px] text-gray-400 font-mono">{p.avg_progress_pct}%</span>
                      </td>
                      <td className="px-4 py-2 text-right text-[10px] text-gray-400 font-mono">{fmtDate(p.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </Layout>
  );
}
